import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { movies, movieCategories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = movieCategories.find(c => c.slug === activeSlug);
  const filteredMovies = activeSlug === 'all'
    ? movies
    : movies.filter(m => m.category === activeSlug);

  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.REACT_APP_NAVIGATE(`/category/${catSlug}`);
  };

  const catPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory?.name || '全部影片'} - 八戒影院`,
    "description": `八戒影院${currentCategory?.name || '全部影片'}分类，高清不缓冲免费在线观看。`,
    "url": `https://bajiecinema.com/category/${activeSlug}`
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://bajiecinema.com/" },
      { "@type": "ListItem", "position": 2, "name": currentCategory?.name || '全部影片' }
    ]
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg data={catPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <div className="w-full px-4 md:px-6 py-6">
        <div className="max-w-[1200px] mx-auto">
          <Breadcrumb
            items={[
              { label: '分类', href: '/category/all' },
              { label: currentCategory?.name || '全部影片' },
            ]}
          />

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
              {currentCategory?.name || '全部影片'}
              <span className="text-sm font-normal text-foreground-500 ml-3">
                共 {filteredMovies.length} 部
              </span>
            </h1>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              <button
                onClick={() => setSortBy('default')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'default' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                默认
              </button>
              <button
                onClick={() => setSortBy('rating')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'rating' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                评分最高
              </button>
              <button
                onClick={() => setSortBy('year')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'year' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                最新发布
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-8">
            {movieCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {sortedMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {sortedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-film-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无影片，请查看其他分类</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}